import React, { useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import LaunchpadStatus from "./LaunchpadStatus";
import EligibilityStatus from "./EligibilityStatus";
import QuizScreen from "./QuizScreen";
import ResultWin from "./ResultWin";
import ResultWrong from "./ResultWrong";
import ResultClosed from "./ResultClosed";
import { LoadingSpinner } from "../common";
import { useEventPolling } from "../../../hooks/newyear";
import {
  getLaunchpadStatus,
  getQuiz,
  submitAnswer,
  checkEligibility,
} from "../../../store/api/newyearApi";
import { SUBMIT_RESULT, POLLING_INTERVAL } from "../../../constants/newyearConstants";

const LaunchpadMain = ({ eventId }) => {
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);

  const [quiz, setQuiz] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);
  const [cooldownUntil, setCooldownUntil] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);

  const {
    data: status,
    loading: statusLoading,
    refetch: refetchStatus,
  } = useEventPolling(
    () => getLaunchpadStatus(eventId),
    {
      enabled: !!eventId,
      interval: POLLING_INTERVAL.STATUS_FAST,
      slowInterval: POLLING_INTERVAL.STATUS_FAST,
      pauseOnHidden: true,
    }
  );

  // 참가 자격 폴링
  const {
    data: eligibilityData,
    loading: eligibilityPollingLoading,
  } = useEventPolling(
    () => checkEligibility("LAUNCHPAD"),
    {
      enabled: !!eventId && isAuthenticated,
      interval: POLLING_INTERVAL.STATUS_FAST,
      slowInterval: POLLING_INTERVAL.STATUS_FAST,
      pauseOnHidden: true,
      onSuccess: (result) => {
        setEligibility(result);
        setEligibilityLoading(false);
      },
      onError: (err) => {
        console.error("참가 자격 확인 실패:", err);
        setEligibility({ hasEligibility: false, message: "참가 자격 확인에 실패했습니다." });
        setEligibilityLoading(false);
      },
    }
  );

  // 초기 eligibility 로딩 상태 설정
  useEffect(() => {
    if (isAuthenticated && !eligibilityData && eligibilityPollingLoading) {
      setEligibilityLoading(true);
    }
  }, [isAuthenticated, eligibilityData, eligibilityPollingLoading]);

  const fetchQuiz = useCallback(async () => {
    if (!eventId || !isAuthenticated) return;
    // 참가 자격이 없으면 퀴즈를 가져오지 않음
    if (!eligibility?.hasEligibility || !eligibility?.canParticipate) return;

    try {
      const quizData = await getQuiz(eventId);
      setQuiz(quizData);
      setCooldownUntil(quizData.cooldownUntil);
    } catch (err) {
      if (err.response?.data?.code === "ALREADY_WINNER") {
        setSubmitResult({ type: SUBMIT_RESULT.ALREADY_WINNER });
      } else {
        setError(err.message || "문제를 불러오는데 실패했습니다.");
      }
    }
  }, [eventId, isAuthenticated, eligibility]);

  useEffect(() => {
    // 퀴즈가 이미 로드되었으면 다시 불러오지 않음
    if (quiz) return;
    if (status?.eventStatus === "OPEN" && isAuthenticated && !submitResult && eligibility?.canParticipate) {
      fetchQuiz();
    }
  }, [status?.eventStatus, isAuthenticated, submitResult, eligibility, fetchQuiz, quiz]);

  const handleSubmitAnswer = useCallback(
    async (answer) => {
      if (!quiz || submitting) return;

      setSubmitting(true);
      setError(null);

      try {
        const result = await submitAnswer(eventId, quiz.questionId, answer);
        setSubmitResult(result);

        if (result.result === SUBMIT_RESULT.WRONG && result.cooldownUntil) {
          setCooldownUntil(result.cooldownUntil);
        }

        refetchStatus();
      } catch (err) {
        setError(err.response?.data?.message || "제출에 실패했습니다.");
      } finally {
        setSubmitting(false);
      }
    },
    [eventId, quiz, submitting, refetchStatus]
  );

  const handleRetry = useCallback(() => {
    setSubmitResult(null);
    setCooldownUntil(null);
    fetchQuiz();
  }, [fetchQuiz]);

  const handleGoHome = useCallback(() => {
    window.location.href = "/";
  }, []);

  if (statusLoading && !status) {
    return <LoadingSpinner message="이벤트 정보를 불러오는 중..." />;
  }

  if (error) {
    return (
      <div className="newyear-launchpad__error">
        <p>{error}</p>
      </div>
    );
  }

  const renderContent = () => {
    if (!isAuthenticated) {
      return (
        <div className="newyear-launchpad__login-required">
          <p>이벤트 참여를 위해 로그인이 필요합니다.</p>
        </div>
      );
    }

    // 참가 자격 로딩 중
    if (eligibilityLoading) {
      return <LoadingSpinner message="참가 자격을 확인하는 중..." />;
    }

    // 참가 자격이 없는 경우
    if (eligibility && !eligibility.hasEligibility) {
      return (
        <div className="newyear-launchpad__no-eligibility">
          <div className="no-eligibility-icon">🎫</div>
          <h3>참가 자격이 없습니다</h3>
          <p>{eligibility.message}</p>
        </div>
      );
    }

    // 이미 참여한 경우
    if (eligibility?.alreadyParticipated) {
      return (
        <div className="newyear-launchpad__already-participated">
          <div className="already-participated-icon">✅</div>
          <h3>이미 참여하셨습니다</h3>
          <p>{eligibility.message}</p>
        </div>
      );
    }

    if (submitResult) {
      switch (submitResult.result) {
        case SUBMIT_RESULT.WIN:
          return <ResultWin result={submitResult} eventId={eventId} />;
        case SUBMIT_RESULT.WRONG:
        case SUBMIT_RESULT.COOLDOWN:
          return (
            <ResultWrong
              cooldownUntil={cooldownUntil || submitResult.cooldownUntil}
              onRetry={handleRetry}
              remainingSlots={status?.remainingSlots}
            />
          );
        case SUBMIT_RESULT.CLOSED:
          return (
            <ResultClosed
              wasCorrect={submitResult.wasCorrect}
              onGoHome={handleGoHome}
            />
          );
        case SUBMIT_RESULT.ALREADY_WINNER:
          return (
            <div className="newyear-launchpad__already-winner">
              <p>이미 당첨되셨습니다!</p>
            </div>
          );
        default:
          break;
      }
    }

    if (status?.eventStatus === "OPEN" && quiz) {
      return (
        <QuizScreen
          question={quiz}
          onSubmit={handleSubmitAnswer}
          loading={submitting}
          cooldownUntil={cooldownUntil}
          disabled={status?.remainingSlots === 0}
        />
      );
    }

    if (status?.eventStatus === "SCHEDULED") {
      return (
        <div className="newyear-launchpad__waiting">
          <p>이벤트 시작 전입니다. 잠시만 기다려주세요.</p>
        </div>
      );
    }

    if (status?.eventStatus === "CLOSED" || status?.eventStatus === "ENDED") {
      return <ResultClosed onGoHome={handleGoHome} />;
    }

    return null;
  };

  return (
    <div className="newyear-launchpad">
      <LaunchpadStatus status={status} />
      {isAuthenticated && eligibility && (
        <EligibilityStatus eligibility={eligibility} />
      )}
      <div className="newyear-launchpad__content">{renderContent()}</div>
    </div>
  );
};

export default LaunchpadMain;
